import { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput"; // Assuming MDInput is a wrapper around Material-UI TextField
import MDButton from "components/MDButton";
import BasicLayout from "layouts/authentication/components/BasicLayout";
import Swal from "sweetalert2";
import 'sweetalert2/dist/sweetalert2.min.css';
import bgImage from "assets/images/backgroundsjust.png";
import logo from "assets/images/logo.png";
import "./login.css";
import { validateEmail, validatePassword } from "../../tables/validations";
import { Select, MenuItem, FormControl, InputLabel, FormHelperText, Stack, IconButton, InputAdornment, Typography } from "@mui/material";
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import api from "core/interceptors/api";
import { useAuth } from "context/auth-context";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";


const OTP_TIMER_SECONDS = 180;

function Basic() {
  const { pathname: routePathname } = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const [role, setRole] = useState("");
  const [roleError, setRoleError] = useState("");
  const [emailError, setEmailError] = useState("");
  const [passwordError, setPasswordError] = useState("");
  const [isOtpMode, setOtpMode] = useState(false);
  const [otp, setOtp] = useState("");
  const [otpError, setOtpError] = useState("");
  const [otpTimeLeft, setOtpTimeLeft] = useState(OTP_TIMER_SECONDS);
  const [resendEnabled, setResendEnabled] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const { pathname } = useLocation();


  const { setIsAuthenticated } = useAuth();

  useEffect(() => {
    let timer;
    if (isOtpMode && otpTimeLeft > 0) {
      timer = setInterval(() => {
        setOtpTimeLeft((prev) => prev - 1);
      }, 1000);
    } else if (otpTimeLeft === 0) {
      setResendEnabled(true);
    }
    return () => clearInterval(timer);
  }, [isOtpMode, otpTimeLeft]);

  const handleRoleChange = useCallback((e) => {
    const selectedRole = e.target.value;
    setRole(selectedRole);
    setRoleError("");

    if (["Admin", "Super Admin", "Services", "Marketing Manager", "Telecaller"].includes(selectedRole)) {
      setOtpMode(false);
      setOtp("");
      setOtpError("");
      setOtpTimeLeft(OTP_TIMER_SECONDS);
      setResendEnabled(false);
    } else {
      setOtpMode(false);
    }
  }, []);

  const handleGetOtp = useCallback(async () => {
    const emailValidation = validateEmail(email);
    const passwordValidation = validatePassword(password);

    setEmailError(emailValidation);
    setPasswordError(passwordValidation);

    if (!role) {
      setRoleError("Error");
      Swal.fire({
        icon: 'warning',
        title: 'Role Required',
        text: 'Please select a role to proceed with login.',
        timer: 3000,
        timerProgressBar: true,
      });
      return;
    } else {
      setRoleError("");
    }

    if (emailValidation || passwordValidation || !role) {
      return;
    }

    setIsLoading(true);
    try {
      if (["Super Admin", "Admin", "Marketing Manager", "Telecaller"].includes(role)) {
        const loginResponse = await api.post('/vendor_login/', {
          email_id: email,
          password: password,
          role: role
        });

        if (loginResponse.data.status === 1) {
          localStorage.setItem("token", loginResponse.data.token);
          localStorage.setItem("email", loginResponse.data.email_id);
          localStorage.setItem("role", loginResponse.data.role);
          localStorage.setItem("code", loginResponse.data.code || "");

          setIsAuthenticated(true);
          localStorage.setItem('isAuthenticated', 'true');

          Swal.fire({
            icon: 'success',
            title: 'Logged in successfully!',
            text: 'Redirecting to the dashboard...',
            timer: 3000,
            timerProgressBar: true,
            didClose: () => {
              navigate('/dashboard');
            },
          });
        } else {
          Swal.fire({
            icon: 'info',
            title: 'Login Failed',
            text: loginResponse.data.message || 'Invalid credentials or an unknown error occurred.',
            timer: 3000,
            timerProgressBar: true,
          });
        }
      } else if (role === "Services" || role === "Vendor") {
        const loginResponse = await api.post(`/vendor_login/`, {
          email_id: email,
          password: password,
          role: role
        });
        if (loginResponse.data.plan_expired) {
          if (loginResponse.data.email_id) {
            localStorage.setItem("renew_email", loginResponse.data.email_id);
          }
          if (loginResponse.data.vendor_type) {
            localStorage.setItem("renew_vendor_type", loginResponse.data.vendor_type); // ✅ Save type
          }
          Swal.fire({
            icon: 'warning',
            title: 'Subscription Expired',
            text: `Your ${loginResponse.data.plan_name || ''} plan has expired. Please renew to continue.`,
            timer: 3000,
            timerProgressBar: true
          }).then(() => {
            navigate('/payment-plans', {
              state: { email: loginResponse.data.email_id || "", vendor_type: loginResponse.data.vendor_type || "" }
            });
          });
          return;
        }


        if (loginResponse.data.status === 0) {
          Swal.fire({
            icon: 'info',
            title: loginResponse.data.message || 'Login failed.',
            text: loginResponse.data.description || 'Please check your credentials.',
            confirmButtonText: 'OK',
            footer: `
              <div style="text-align: center; font-size: 14px; color: #555;">
                For queries, contact:<br />
                <strong>+91-9876543210</strong> or <strong>+91-9123456780</strong>
              </div>
            `
          });
        } else if (loginResponse.data.status === 1) {
          localStorage.setItem("mobile_number", loginResponse.data.mobile_number);
          localStorage.setItem("token", loginResponse.data.token);
          localStorage.setItem("email", loginResponse.data.email_id);
          localStorage.setItem("role", loginResponse.data.role);
          localStorage.setItem("username", loginResponse.data.username);

          if (role === "Vendor") {
            /*
            const otpResponse = await api.post("/otp_email/generate_mobile_otp", {
                mobile_number: loginResponse.data.mobile_number,
            });

            if (otpResponse.data.status === 1) {
              setOtpMode(true);
              setOtpTimeLeft(OTP_TIMER_SECONDS);
              setResendEnabled(false);
              Swal.fire({
                icon: 'success',
                title: 'OTP Sent!',
                text: 'Please check your registered mobile number for the OTP.',
                timer: 3000,
                timerProgressBar: true,
              });
            } else {
              Swal.fire({
                icon: 'info',
                title: 'OTP Generation Failed',
                text: otpResponse.data.message || 'Could not generate OTP. Please try again.',
                timer: 3000,
                timerProgressBar: true,
              });
              setIsAuthenticated(false);
              localStorage.removeItem('isAuthenticated');
            }
            */
            setIsAuthenticated(true);
            localStorage.setItem('isAuthenticated', 'true');
            Swal.fire({
              icon: 'success',
              title: 'Credentials Validated',
              text: 'Redirecting to Mobile OTP verification',
              timer: 3000,
              timerProgressBar: true,
              didClose: () => {
                navigate('/mobile');
              },
            });

          } else if (role === "Services") {
            setIsAuthenticated(true);
            localStorage.setItem('isAuthenticated', 'true');
            Swal.fire({
              icon: 'success',
              title: 'Logged in successfully!',
              text: 'Redirecting to the dashboard...',
              timer: 3000,
              timerProgressBar: true,
              didClose: () => {
                navigate('/dashboard');
              },
            });
          }
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      Swal.fire({
        icon: 'error',
        title: 'Login Failed',
        text: error.response?.data?.message || 'An unexpected error occurred. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [email, password, role, navigate, setIsAuthenticated]);

  const handleForgotPassword = useCallback(() => {
    navigate('/forgot-password');
  }, [navigate]);

  const handleOtpChange = useCallback((e) => {
    setOtp(e.target.value);
    setOtpError("");
  }, []);

  const handleEmailChange = useCallback((e) => {
    const value = e.target.value;
    setEmail(value);
    setEmailError(validateEmail(value));
  }, []);

  const handlePasswordChange = useCallback((e) => {
    const value = e.target.value;
    setPassword(value);
    setPasswordError(validatePassword(value));
  }, []);

  const handleClickShowPassword = () => setShowPassword((show) => !show);
  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handleVerifyOtp = useCallback(async () => {
    if (!otp) {
      setOtpError("OTP cannot be empty.");
      return;
    }
    setIsLoading(true);
    try {
      const identifier = localStorage.getItem("email") || localStorage.getItem("mobile_number");
      if (!identifier) {
        Swal.fire({
          icon: 'error',
          title: 'Verification Error',
          text: 'User identifier not found for OTP verification. Please try logging in again.',
        });
        setIsLoading(false);
        return;
      }

      // **UNCOMMENT and use your actual API endpoint for OTP verification**
      // const verifyResponse = await api.post('/otp_email/verify_mobile_otp', { // Adjust endpoint as needed
      //   identifier: identifier, // Use email_id or mobile_number depending on your backend
      //   otp: otp
      // });

      const verifyResponse = { data: { status: 1, message: "OTP verified successfully!" } }; // Mock response

      if (verifyResponse.data.status === 1) {
        setIsAuthenticated(true);
        localStorage.setItem('isAuthenticated', 'true');
        Swal.fire({
          icon: 'success',
          title: 'OTP Verified!',
          text: verifyResponse.data.message,
          timer: 2000,
          timerProgressBar: true,
          didClose: () => {
            navigate('/dashboard');
          },
        });
      } else {
        Swal.fire({
          icon: 'error',
          title: 'OTP Verification Failed',
          text: verifyResponse.data.message || 'Invalid OTP. Please try again.',
        });
        setOtpError(verifyResponse.data.message || "Invalid OTP.");
      }
    } catch (error) {
      console.error("OTP verification error:", error);
      Swal.fire({
        icon: 'error',
        title: 'Verification Failed',
        text: error.response?.data?.message || 'An error occurred during OTP verification. Please try again.',
      });
    } finally {
      setIsLoading(false);
    }
  }, [otp, navigate, setIsAuthenticated]);

  const handleResendOtp = useCallback(async () => {
    Swal.fire({
      icon: 'info',
      title: 'Resending OTP...',
      text: 'A new OTP has been sent to your registered mobile number.',
      timer: 3000,
      timerProgressBar: true,
    });
    setOtpTimeLeft(OTP_TIMER_SECONDS);
    setResendEnabled(false);
    setOtp("");
    setOtpError("");
  }, []);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  return (
    <BasicLayout image={bgImage}>
      <Card>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          textAlign="center"
        >
          <MDBox display="flex" alignItems="center" justifyContent="center" gap={1} mt={1}>
            <div style={{
              width: '50px',
              height: '50px',
              borderRadius: '50%',
              overflow: 'hidden',
              backgroundColor: 'white',
              marginRight: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}>
              <img
                src={logo}
                alt="Company Logo"
                style={{
                  width: '80%',
                  height: '80%',
                  objectFit: 'contain'
                }}
              />
            </div>

            <MDTypography variant="h4" fontWeight="medium" color="white">
              {isOtpMode ? "Enter OTP" : (routePathname === "/login" ? "Sign In" : "Admin | Sign In")}
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox pt={4} pb={3} px={3}>
          <MDBox component="form" role="form" autocomplete="off"> {/* Added autocomplete="off" here */}
            {!isOtpMode && (
              <>
                <MDBox mb={2}>
                  <MDBox>
                    <MDInput
                      type="email"
                      label="Email"
                      fullWidth
                      value={email}
                      onChange={handleEmailChange}
                      autocomplete="off"
                      InputProps={{
                        sx: {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: emailError ? 'red' : '',
                          },
                        },
                      }}
                    />
                    {emailError && (
                      <Typography variant="caption" color="error" sx={{ ml: '14px', mt: '4px', display: 'block' }}>
                        {emailError}
                      </Typography>
                    )}
                  </MDBox>
                </MDBox>

                <MDBox mb={2}>
                  <MDInput
                    type={showPassword ? 'text' : 'password'}
                    label="Password"
                    fullWidth
                    value={password}
                    onChange={handlePasswordChange}
                    autocomplete="new-password"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                            edge="end"
                            sx={{ p: 0.5 }}
                          >
                            {showPassword ? <VisibilityOff /> : <Visibility />}
                          </IconButton>
                        </InputAdornment>
                      ),
                      sx: {
                        '& .MuiOutlinedInput-notchedOutline': {
                          borderColor: passwordError ? 'red' : '',
                        },
                      },
                    }}
                  />
                  {passwordError && (
                    <Typography variant="caption" color="error" sx={{ ml: '14px', mt: '4px', display: 'block' }}>
                      {passwordError}
                    </Typography>
                  )}
                </MDBox>

              </>
            )}

            {/* Role Dropdown */}
            {!isOtpMode && (
              <MDBox mb={2}>
                <FormControl fullWidth error={!!roleError}>
                  <InputLabel id="role-select-label">Role</InputLabel>
                  {routePathname === "/admin/login" ? (
                    <Select
                      labelId="role-select-label"
                      value={role}
                      onChange={handleRoleChange}
                      label="Role"
                      sx={{ height: 45 }}
                      displayEmpty
                      autocomplete="off"
                    >
                      <MenuItem value="Super Admin">Super Admin</MenuItem>
                      <MenuItem value="Admin">Admin</MenuItem>
                      <MenuItem value="Marketing Manager">Marketing Manager</MenuItem>
                      <MenuItem value="Telecaller">Telecaller</MenuItem>
                    </Select>
                  ) : (
                    <Select
                      labelId="role-select-label"
                      value={role}
                      onChange={handleRoleChange}
                      label="Role"
                      sx={{ height: 45 }}
                      displayEmpty
                      autocomplete="off" // Add autocomplete="off" to Select too
                    >
                      <MenuItem value="Vendor">Vendor</MenuItem>
                      <MenuItem value="Services">Services</MenuItem>
                    </Select>
                  )}
                  {!!roleError && <FormHelperText>Role is required</FormHelperText>}
                </FormControl>
              </MDBox>
            )}

            {isOtpMode && (
              <MDBox mb={2}>
                <MDInput
                  type="text"
                  label="OTP"
                  fullWidth
                  value={otp}
                  onChange={handleOtpChange}
                  error={!!otpError}
                  helperText={otpError || `Enter OTP (expires in ${formatTime(otpTimeLeft)})`}
                  autocomplete="off" // Add autocomplete="off" to OTP field
                />
              </MDBox>
            )}

            {isOtpMode && otpTimeLeft === 0 && resendEnabled && (
              <MDBox mt={2} textAlign="center">
                <MDButton
                  variant="text"
                  color="info"
                  onClick={handleResendOtp}
                  disabled={isLoading}
                >
                  Resend OTP
                </MDButton>
              </MDBox>
            )}

            <MDButton
              variant="gradient"
              color="info"
              fullWidth
              onClick={isOtpMode ? handleVerifyOtp : handleGetOtp}
              disabled={isLoading || (!isOtpMode && (!email || !password || !role)) || (isOtpMode && !otp)}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : isOtpMode ? (
                "Verify OTP"
              ) : (
                "Sign in"
              )}
            </MDButton>

            {routePathname !== "/admin/login" && (
              <MDBox mt={1} textAlign="right">
                <MDTypography
                  variant="button"
                  sx={{ color: "#FF0000", fontSize: "0.8rem", cursor: "pointer" }}
                  fontWeight="small"
                  onClick={handleForgotPassword}
                >
                  Forgot Password?
                </MDTypography>
              </MDBox>
            )}

            <MDBox mt={3} mb={2} textAlign="center">
              {isOtpMode ? (
                <MDTypography
                  variant="button"
                  color="info"
                  fontWeight="medium"
                  textGradient
                  sx={{ cursor: "pointer", fontSize: "1rem" }}
                  onClick={() => {
                    setOtpMode(false);
                    setOtp("");
                    setOtpError("");
                    setOtpTimeLeft(OTP_TIMER_SECONDS);
                    setResendEnabled(false);
                  }}
                >
                  ⬅ Back to Sign In
                </MDTypography>
              ) : (
                <Stack spacing={1.5} alignItems="center">
                  {routePathname === "/login" ? (
                    <>
                      <MDTypography variant="button" color="text">
                        Don't have an account?{" "}
                        <MDTypography
                          component={Link}
                          to="/sign_up"
                          variant="button"
                          color="info"
                          fontWeight="medium"
                          textGradient
                          sx={{ textDecoration: "underline" }}
                        >
                          Sign Up
                        </MDTypography>
                      </MDTypography>


                    </>
                  ) : (
                    <MDTypography variant="button" color="text">
                      Back to login?{" "}
                      <MDTypography
                        component={Link}
                        to="/login"
                        variant="button"
                        color="info"
                        fontWeight="medium"
                        textGradient
                        sx={{ textDecoration: "underline" }}
                      >
                        Login
                      </MDTypography>
                    </MDTypography>
                  )}
                </Stack>
              )}
            </MDBox>
          </MDBox>
        </MDBox>
        <MDBox
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            p: 2,
            borderTop: "1px solid rgba(0, 0, 0, 0.12)", // optional separator line
          }}
        >
          <MDTypography variant="button" color="text">
            Need support?{" "}
            <MDTypography
              component={Link}
              to="/contact"
              variant="button"
              color="info"
              fontWeight="medium"
              textGradient
              sx={{ textDecoration: "underline", cursor: "pointer" }}
            >
              Contact Us
            </MDTypography>
          </MDTypography>
        </MDBox>
        <MDBox
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            px: 2,
            pb: 1,
          }}
        >
          {/* 🔐 USER LOGIN → SHOW ADMIN ICON */}
          {pathname === "/login" && (
            <IconButton
              size="small"
              sx={{
                opacity: 0.6,
                "&:hover": { opacity: 1 },
              }}
              onClick={() => navigate("/admin/login")}
            >
              <AdminPanelSettingsIcon fontSize="small" />
            </IconButton>
          )}

          {/* 👤 ADMIN LOGIN → SHOW USER BACK ICON */}
          {pathname === "/admin/login" && (
            <IconButton
              size="small"
              sx={{
                opacity: 0.6,
                "&:hover": { opacity: 1 },
              }}
              onClick={() => navigate("/login")}
            >
              <ArrowBackIcon fontSize="small" />
            </IconButton>
          )}
        </MDBox>

      </Card>
    </BasicLayout>
  );
}

export default Basic;